import { memo, useCallback, useState } from 'react';
import { Dialog } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { uploadAcademicImage } from '../../../../api/talentsApi';
import STRING_KEYS from '../../../../language/keys';
import { getServerError } from '../../../../utils';
import { useCreateNewManualEducation } from '../../../../hooks/useTalentAcademic';
import ModalActions from './ModalActions';
import ModalContent from './ModalContent';
import ModalTitle from './ModatTitle';

type ManualEducationModalProps = {
    isOpen: boolean,
    onCloseCreateNewEducationModal: () => void,
    selectedId: string,
    educationFieldName: string,
    onEducationCreated: (updatedObject:
      UpdateValues<string | boolean | null | DateObject>, id: string) => void,
}

const ManualEducationModal = ({
  isOpen,
  onCloseCreateNewEducationModal,
  selectedId,
  onEducationCreated,
  educationFieldName,
}: ManualEducationModalProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const [educationName, setEducationName] = useState<string>(educationFieldName);
  const [educationImage, setEducationImage] = useState<string>('');
  const [isUploading, setIsUploadLoading] = useState<boolean>(false);
  const [serverError, setError] = useState<
  {hasError: boolean, errorMessage: string, }>({
    hasError: false,
    errorMessage: '',
  });
  const [isFieldError, setIsFieldError] = useState<boolean>(false);

  const handleCloseModal = () => {
    onCloseCreateNewEducationModal();
    setEducationName('');
    setEducationImage('');
  };

  const createNewEducationManually = useCreateNewManualEducation(() => {
    onEducationCreated({
      institutionName: educationName,
      institutionLogo: educationImage,
      institutionId: '',
    }, selectedId);
    onCloseCreateNewEducationModal();
  },
  setError,
  );

  const handleChangeEducationName = (enteredEducationName: string) => {
    setEducationName(enteredEducationName);
    if (enteredEducationName.trim() === '') {
      setIsFieldError(true);
    } else {
      setIsFieldError(false);
    }
  };

  const handleCreateEducation = () => {
    createNewEducationManually.mutate({
      name: educationName,
      logo: educationImage,
    });
  };

  const handleInputUploadImage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const myFile = e.target.files ? e.target.files[0] : '';
    const formData = new FormData();
    formData.append('file', myFile);
    setIsUploadLoading(true);
    uploadAcademicImage(formData).then((data: { fileUrl: string }) => {
      setEducationImage(data.fileUrl);
    })
      .catch((error) => {
        const errorMessage = getServerError(error);
        setError({
          hasError: true,
          errorMessage,
        },
        );
      })
      .finally(() => {
        setIsUploadLoading(false);
      });
  }, [setIsUploadLoading, setError]);

  return (
    <Dialog open={ isOpen } onClose={ handleCloseModal }>
      <ModalTitle
        title={ translate(STRING_KEYS.CREATE_NEW_EDUCATION_INSTITUTE) }
        onCloseModal={ handleCloseModal }
      />
      <ModalContent
        isLoading={ createNewEducationManually.isLoading }
        isError={ serverError.hasError }
        errorMessage={ serverError.errorMessage }
        isUploadingImage={ isUploading }
        onUploadImage={ handleInputUploadImage }
        educationImage={ educationImage }
        isFieldError={ isFieldError }
        educationName={ educationName }
        onChangeEducationName={ handleChangeEducationName }
      />
     
      <ModalActions
        onCreateEducation={ handleCreateEducation }
        onCloseModal={ handleCloseModal }
        isDisabled={ educationName.trim() === '' }
      />
    </Dialog>
  );
};

export default memo(ManualEducationModal);
